/**
 * The house format, as you leave the field.
 *
 * The server is what makes this true — `app/Domain/Formatting/Normalise.php`
 * applies it at the model boundary, where no form, import or API client can
 * get round it. This is the same set of rules in the browser so that what you
 * see after tabbing out is what was stored, rather than the field appearing to
 * keep what you typed until the next time the page loads.
 *
 * Kept deliberately in step with the PHP. If a rule changes there, it changes
 * here; the tests on both sides use the client's own examples.
 */

/** Particles that stay lower case inside a name: van der Berg, de Silva. */
const PARTICLES = new Set([
  'van', 'von', 'der', 'den', 'de', 'di', 'da', 'del', 'della', 'du', 'la', 'le', 'bin', 'binti', 'al',
]);

/** Prefixes whose next letter is capitalised: McDonald, O'Brien, MacLeod. */
const PREFIXES = ['mc', 'mac', "o'", "d'"];

/** Street types, written out. Expanded because people read these, not sorting machines. */
const STREET_TYPES: Record<string, string> = {
  st: 'Street', str: 'Street',
  rd: 'Road',
  ave: 'Avenue', av: 'Avenue',
  hwy: 'Highway', hway: 'Highway',
  cres: 'Crescent', cr: 'Crescent', cresc: 'Crescent',
  ct: 'Court', crt: 'Court',
  dr: 'Drive', drv: 'Drive',
  pde: 'Parade', para: 'Parade',
  pl: 'Place',
  tce: 'Terrace', ter: 'Terrace',
  ln: 'Lane', lane: 'Lane',
  blvd: 'Boulevard', bvd: 'Boulevard', bld: 'Boulevard',
  cct: 'Circuit', cir: 'Circuit',
  esp: 'Esplanade',
  gr: 'Grove', gve: 'Grove',
  sq: 'Square',
  wy: 'Way',
  cl: 'Close',
  mw: 'Mews',
  rdg: 'Ridge',
  tr: 'Track', trk: 'Track',
};

/** Sub-dwelling prefixes, written out and moved to the front. */
const SUB_DWELLING: Record<string, string> = {
  l: 'Level', lvl: 'Level', lev: 'Level', level: 'Level',
  u: 'Unit', unit: 'Unit',
  apt: 'Apartment', apartment: 'Apartment',
  ste: 'Suite', suite: 'Suite',
  shop: 'Shop',
  fl: 'Floor', flr: 'Floor', floor: 'Floor',
  g: 'Ground Floor',
};

const STATES: Record<string, string> = {
  victoria: 'VIC',
  'new south wales': 'NSW',
  queensland: 'QLD',
  'south australia': 'SA',
  'western australia': 'WA',
  tasmania: 'TAS',
  'northern territory': 'NT',
  'australian capital territory': 'ACT',
};

/** Trimmed, with runs of whitespace collapsed. */
export function squash(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

const capitalise = (word: string) =>
  word === '' ? '' : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/** Two or three letters already in capitals is an initialism, not a word. */
const isInitialism = (word: string) => word.length <= 3 && word === word.toUpperCase() && /[A-Z]/.test(word);

/**
 * A person's name: Title Case, however it was typed.
 *
 * Anything with a digit in it is left exactly as typed — that is not a name.
 * Initials are left alone too, or "VA" becomes "Va".
 */
export function name(value: string): string {
  const squashed = squash(value);
  if (squashed === '' || /\d/.test(squashed)) return squashed;

  return squashed
    .split(/(\s|-)/)
    .map((part, index) => {
      if (part.trim() === '') return part;
      if (isInitialism(part)) return part;

      const lower = part.toLowerCase();

      // A particle keeps its place and its case, unless it opens the name.
      if (index > 0 && PARTICLES.has(lower)) return lower;

      for (const prefix of PREFIXES) {
        if (lower.startsWith(prefix) && lower.length > prefix.length) {
          return (
            lower.charAt(0).toUpperCase()
            + lower.slice(1, prefix.length)
            + lower.charAt(prefix.length).toUpperCase()
            + lower.slice(prefix.length + 1)
          );
        }
      }

      // An apostrophe inside a word capitalises what follows it.
      return lower.split("'").map((piece) => (piece === '' ? '' : piece.charAt(0).toUpperCase() + piece.slice(1))).join("'");
    })
    .join('');
}

/**
 * A company or trading name — gentler than a person's.
 *
 * "REQ Construction", "DAAC" and "TKC" are real companies in this client's
 * directory, so an all-caps or mixed-case name is left as typed and only an
 * all-lower-case one is lifted.
 */
export function company(value: string): string {
  const squashed = squash(value);
  if (squashed === '' || squashed !== squashed.toLowerCase()) return squashed;

  return squashed.split(' ').map((word) => (word === '' ? '' : word.charAt(0).toUpperCase() + word.slice(1))).join(' ');
}

export function email(value: string): string {
  return squash(value).toLowerCase();
}

/** An Australian phone or mobile, in the format people write it. */
export function phone(value: string): string {
  const squashed = squash(value);
  if (squashed === '') return squashed;

  let digits = squashed.replace(/[^\d+]/g, '');
  // +61 is the same number as 0, written for somebody overseas.
  if (digits.startsWith('+61')) digits = `0${digits.slice(3)}`;
  const plain = digits.replace(/^\+/, '');

  if (/^04\d{8}$/.test(plain)) return `${plain.slice(0, 4)} ${plain.slice(4, 7)} ${plain.slice(7)}`;
  if (/^0[23578]\d{8}$/.test(plain)) return `${plain.slice(0, 2)} ${plain.slice(2, 6)} ${plain.slice(6)}`;
  if (/^1[38]00\d{6}$/.test(plain)) return `${plain.slice(0, 4)} ${plain.slice(4, 7)} ${plain.slice(7)}`;
  if (/^13\d{4}$/.test(plain)) return `${plain.slice(0, 2)} ${plain.slice(2, 4)} ${plain.slice(4)}`;

  return squashed;
}

/** An ABN in the shape the ATO prints: 12 345 678 901. */
export function abn(value: string): string {
  const squashed = squash(value);
  const digits = squashed.replace(/\D/g, '');
  if (digits.length !== 11) return squashed;

  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
}

/** A state, as the letters everyone writes. A full name is abbreviated, not shouted. */
export function state(value: string): string {
  const squashed = squash(value);
  if (squashed === '') return squashed;

  const full = STATES[squashed.toLowerCase()];
  if (full) return full;

  return squashed.length <= 3 ? squashed.toUpperCase() : squashed;
}

export function suburb(value: string): string {
  return name(value);
}

/**
 * Pull a level or unit out of a part, wherever in it they wrote it.
 *
 * Returns the sub-dwelling and what is left of the part, or null when there is
 * none. Matching only a part that was *entirely* "L2" was too strict: a level
 * written on the end stayed there in lower case, and stopped the street type
 * expanding, because a street type is only expanded where it ends its part.
 */
function subDwelling(part: string): [string, string] | null {
  const words = part.split(/\s+/);

  const without = (index: number, length: number) => {
    const kept = [...words];
    kept.splice(index, length);
    return kept.join(' ').trim();
  };

  for (let index = 0; index < words.length; index += 1) {
    const word = words[index] ?? '';

    // "L2", "Lvl2", "U4b" written as one word.
    const joined = /^([A-Za-z]{1,9})\.?(\d+[A-Za-z]?)$/.exec(word);
    if (joined) {
      const name = SUB_DWELLING[(joined[1] ?? '').toLowerCase()];
      if (name) return [`${name} ${(joined[2] ?? '').toUpperCase()}`, without(index, 1)];
    }

    // "Level 2", "Unit 4b" written as two.
    const next = words[index + 1];
    const name = SUB_DWELLING[word.toLowerCase().replace(/\.+$/, '')];
    if (name && next && /^\d+[A-Za-z]?$/.test(next)) {
      return [`${name} ${next.toUpperCase()}`, without(index, 2)];
    }
  }

  return null;
}

/** Title Case one comma part, and write its street type out. */
function addressPart(part: string): string {
  const words = part.split(' ');
  const last = words.length - 1;

  return words
    .map((word, index) => {
      // Anything carrying a digit is a number, not a word — except the letter
      // on a unit or street number, which is written in capitals.
      if (word === '' || /\d/.test(word)) {
        return /^\d+[a-zA-Z]$/.test(word) ? word.toUpperCase() : word;
      }

      // A street type only counts where it ends the part, so "St Kilda Road"
      // keeps its saint.
      const bare = word.toLowerCase().replace(/\.+$/, '');
      if (index === last && index > 0 && STREET_TYPES[bare]) return STREET_TYPES[bare];

      if (isInitialism(word)) return word;

      // Both halves of a hyphenated name: Dandenong-Frankston Road is two
      // places, and only capitalising the first made "Dandenong-frankston".
      return word.split('-').map(capitalise).join('-');
    })
    .join(' ');
}

/**
 * A street address, in one house format.
 *
 * `L2, 365 Bourke Street, Melbourne`, `365 Bourke Street - L2` and
 * `Level 2, 365 Bourke St, Melbourne` all become
 * `Level 2, 365 Bourke Street, Melbourne`.
 */
export function address(value: string): string {
  let squashed = squash(value);
  if (squashed === '') return squashed;

  // " - L2" is a trailing level. Whitespace is required on both sides, because
  // a hyphen without it is a street number range: "1147-1151 High Street".
  squashed = squashed.replace(/\s+[-–—]\s+/g, ', ');

  const parts = squashed.split(',').map(squash).filter(Boolean);

  /*
    "2/333 Dandenong-Frankston Road" is how an Australian unit is written, and
    under one house format it is the same address as "Unit 2, 333
    Dandenong-Frankston Road". Only the first part is read this way: a slash
    later in an address is something else.
  */
  const unit = parts.length ? /^(\d+[A-Za-z]?)\s*\/\s*(\d+[A-Za-z]?)(\s+.*)?$/.exec(parts[0] ?? '') : null;
  if (unit) {
    parts[0] = `${unit[2] ?? ''}${unit[3] ?? ''}`.trim();
    parts.unshift(`Unit ${(unit[1] ?? '').toUpperCase()}`);
  }

  const front: string[] = [];
  const rest: string[] = [];

  for (const part of parts) {
    const sub = subDwelling(part);

    if (!sub) {
      rest.push(addressPart(part));
      continue;
    }

    // The level goes to the front; whatever else was in that part stays where
    // it was, so "642 Collins Street l2" keeps its street.
    const [dwelling, remainder] = sub;
    front.push(dwelling);
    if (remainder !== '') rest.push(addressPart(remainder));
  }

  return [...front, ...rest].join(', ');
}

/** Free text in a sentence box. Only the first letter, and only if it is all lower case. */
export function sentence(value: string): string {
  const squashed = squash(value);
  if (squashed === '' || squashed !== squashed.toLowerCase()) return squashed;

  return squashed.charAt(0).toUpperCase() + squashed.slice(1);
}

/** A label, trade or category chosen from a list people also type into. */
export function label(value: string): string {
  return company(value);
}

export type HouseFormat =
  | 'name' | 'company' | 'email' | 'phone' | 'abn'
  | 'state' | 'suburb' | 'address' | 'sentence' | 'label' | 'squash';

const RULES: Record<HouseFormat, (value: string) => string> = {
  name, company, email, phone, abn, state, suburb, address, sentence, label, squash,
};

/** Apply a rule by name. One mapping, so a field names the rule and nothing else. */
export function houseFormat(rule: HouseFormat, value: string): string {
  return RULES[rule](value);
}

/**
 * What the house format looks like, as an example in the empty field.
 *
 * A field that silently rewrites what you typed is worse than one that tells
 * you the shape first, so a field given a `format` shows the format. Kept
 * beside the rules rather than written into each form: there are forty-odd
 * fields and they should not each carry their own idea of what good looks
 * like. A caller that passes its own placeholder still wins.
 */
export const FORMAT_PLACEHOLDER: Record<HouseFormat, string> = {
  address: 'Level 2, 365 Bourke Street, Melbourne',
  suburb: 'Melbourne',
  state: 'VIC',
  phone: '0412 345 678',
  email: 'name@company.com.au',
  abn: '12 345 678 901',
  name: 'Jane Smith',
  company: 'REQ Construction',
  label: '',
  sentence: '',
  squash: '',
};

/**
 * The rule in words, for the line under the field.
 *
 * Only where the shape is not obvious from the example — nobody needs telling
 * what an email looks like.
 */
export const FORMAT_HINT: Partial<Record<HouseFormat, string>> = {
  address: 'Unit or level first, street type written out. Tidied when you leave the field.',
  phone: 'Australian mobile or landline. Spaced when you leave the field.',
  abn: 'Eleven digits.',
  state: 'The state or territory — VIC, NSW, QLD.',
};

/* Structured address ---------------------------------------------------------

   The house format applied after the fact can only tidy what it is given. The
   same format expressed as fields cannot be got wrong in the first place: a
   number is a number and a street type is chosen, so "642 Collins Street …
   l2222" has nowhere to land.

   Not every address fits, though — "Berth 3, Port Hedland WA 6721" is on this
   client's own register, and so are PO boxes. Those keep the single line, and
   `parseAddress` says so rather than mangling them into fields.
*/

/** Every street type, written out, for the dropdown. */
export const STREET_TYPE_OPTIONS = Array.from(new Set(Object.values(STREET_TYPES))).sort();

export interface AddressParts {
  /** "Level 2", "Unit 4B" — empty when there is none. */
  sub: string;
  /** "365", "65A", "1147-1151". */
  number: string;
  /** "Bourke", "Dandenong-Frankston". */
  street: string;
  /** One of STREET_TYPE_OPTIONS. */
  type: string;
  /** Anything after the street — a suburb typed into the same line. */
  rest: string;
}

export const EMPTY_ADDRESS: AddressParts = { sub: '', number: '', street: '', type: '', rest: '' };

/** The parts, back into the one line that is stored. */
export function composeAddress(parts: AddressParts): string {
  const street = [parts.number.trim(), parts.street.trim(), parts.type.trim()].filter(Boolean).join(' ');

  return [parts.sub.trim(), street, parts.rest.trim()].filter(Boolean).join(', ');
}

/**
 * The stored line, back into parts — or null when it is not that shape.
 *
 * Null is the answer for a PO box, a berth, a lot number: addresses that are
 * real and simply are not "number, street, type". The field offers the single
 * line for those instead of pretending they parse.
 */
export function parseAddress(value: string): AddressParts | null {
  const tidied = address(value);
  if (tidied === '') return EMPTY_ADDRESS;

  const parts = tidied.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return EMPTY_ADDRESS;

  let sub = '';
  let index = 0;

  // A level or unit, already moved to the front by `address()`.
  const first = parts[0] ?? '';
  if (/^(Level|Unit|Apartment|Suite|Shop|Floor|Ground Floor)\s/.test(first)) {
    sub = first;
    index = 1;
  }

  const streetLine = parts[index] ?? '';
  const types = STREET_TYPE_OPTIONS.join('|');
  /*
    String.raw, not a plain template literal.

    A template literal eats the backslash — `\d` is not an escape it knows, so
    it becomes a bare "d" and the pattern quietly matches the letter instead of
    a digit. Every address then failed to parse and the fields came up empty.
  */
  const match = new RegExp(
    String.raw`^(\d+[A-Za-z]?(?:-\d+[A-Za-z]?)?)\s+(.+)\s+(${types})$`,
  ).exec(streetLine);

  if (!match) return null;

  return {
    sub,
    number: match[1] ?? '',
    street: match[2] ?? '',
    type: match[3] ?? '',
    rest: parts.slice(index + 1).join(', '),
  };
}
